import {ChartData, ChartDataset, ChartOptions, ScatterDataPoint,} from 'chart.js';
import 'chartjs-adapter-date-fns';
import {useEffect, useState} from "react";
import {Chart, ChartProps} from '../Chart/Chart';
import {useTranslation} from "react-i18next";
import {
    cargoSubtype,
    CargoSubtype,
    containerLevelHistoryTypes,
    ContainerStatusEntity,
    LevelEntity,
    LevelType
} from "@ots-frontend/ots-data";
import {TFunction} from "i18next";
import {getCargoSubtypeColor} from "../../../util/cargo-subtype-ui-util";

export type ContainerStatusChartProps = Omit<ChartProps<'line'>, 'type' | 'options' | 'data'> & {
    status: ContainerStatusEntity
};

export const ContainerStatusChart = (props: ContainerStatusChartProps) => {
    const {status, ...rest} = props;

    const {t,} = useTranslation(["common"])

    const [chartOptions, setChartOptions] = useState<
        ChartOptions<'line'> | undefined
    >(undefined);

    const [chartData, setChartData] = useState<
        ChartData<'line'> | undefined
    >(undefined);

    useEffect(() => {
        setChartOptions(getChartOptions(t, status.containerVolume))
    }, [t, status.containerVolume])

    useEffect(() => {
        setChartData(getChartData(status, t))
    }, [status, t])

    return <Chart {...rest} type="line" options={chartOptions} data={chartData}/>
};


const getChartOptions = (t: TFunction<"common"[], any, "common"[]>, max: number | undefined): ChartOptions<'line'> => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        elements: {
            point: {
                radius: 0
            }
        },
        plugins: {
            datalabels: {
                display: false
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: t<string>("commons.entity.containerstatus.chart.xaxis.label", "Time"),
                },
                type: 'time',
                time: {
                    unit: 'hour',
                },
            },
            y: {
                title: {
                    display: true,
                    text: t<string>("commons.entity.containerstatus.chart.yaxis.label", "Level"),
                },
                beginAtZero: true,
                max: max,
            },
        },
    };
};

const getChartData = (
    status: ContainerStatusEntity,
    t: TFunction<"common"[], any, "common"[]>,
): ChartData<'line'> => {
    const chartDataSets: ChartDataset<'line'>[] = [];

    const cargoSubtypes: CargoSubtype[] = [cargoSubtype.water, cargoSubtype.oil]

    for (const cargoSubtype of cargoSubtypes) {
        chartDataSets.push({
            label: t<string>(cargoSubtype.displayValue.id, cargoSubtype.displayValue.defaultMessage),
            fill: false,
            data: getChartPoints(status.getHistory(cargoSubtype, containerLevelHistoryTypes.meter), "volume"),
            borderColor: getCargoSubtypeColor(cargoSubtype),
            borderWidth: 3,
        });
    }

    return {
        datasets: chartDataSets,
    };
};

const getChartPoints = (levels: LevelEntity[] | undefined, levelType: LevelType): ScatterDataPoint[] => {
    if (!levels)
        return []

    return levels
        .filter((l) => typeof l.getLevel(levelType) !== 'undefined')
        .map((l) => {
            return {
                x: l.dttm!,
                y: l.getLevel(levelType)!,
            };
        });
};
